<!--
  Activity Center — header indicator for background jobs (Feature: caption
  translation jobs today; audio dubs later). Renders NOTHING when nothing is
  running; when a job is active it shows a spinning badge that opens a dropdown
  listing each job with a live progress bar + a cancel button.

  Data comes from the jobs store (polls GET /api/jobs). The player re-attaches
  to an in-flight translation independently — this component is purely the
  household-wide "what's working right now" surface + cancel control.
-->
<template>
  <div v-if="jobsStore.hasActive" ref="root" class="relative">
    <button
      type="button"
      class="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      :class="{ 'bg-gray-100 dark:bg-gray-700': open }"
      :aria-label="`${jobsStore.activeCount} background task${jobsStore.activeCount === 1 ? '' : 's'} running`"
      :title="uiStore.tvMode ? '' : 'Background activity'"
      @click.stop="open = !open"
    >
      <!-- spinning ring -->
      <svg
        class="w-5 h-5 text-gray-600 dark:text-gray-300 animate-spin"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <circle class="opacity-20" cx="12" cy="12" r="9" stroke="currentColor" stroke-width="3" />
        <path
          d="M21 12a9 9 0 0 0-9-9"
          stroke="currentColor"
          stroke-width="3"
          stroke-linecap="round"
        />
      </svg>
      <!-- count badge -->
      <span
        class="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center text-[10px] font-bold leading-none text-white bg-blue-600 rounded-full ring-2 ring-white dark:ring-gray-900"
      >
        {{ jobsStore.activeCount }}
      </span>
    </button>

    <!-- dropdown -->
    <div
      v-if="open"
      class="absolute right-0 mt-2 w-80 max-w-[calc(100vw-1rem)] bg-white dark:bg-gray-800 rounded-xl shadow-2xl ring-1 ring-black/5 dark:ring-white/10 z-50 overflow-hidden"
    >
      <div class="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700">
        <p class="text-sm font-semibold text-gray-900 dark:text-white">Activity</p>
        <p class="text-xs text-gray-500 dark:text-gray-400">
          {{ jobsStore.activeCount }} task{{ jobsStore.activeCount === 1 ? '' : 's' }} running
        </p>
      </div>
      <ul class="max-h-80 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
        <li v-for="job in jobsStore.jobs" :key="job.id" class="px-4 py-3">
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0 flex-1">
              <p class="text-sm font-medium text-gray-900 dark:text-white truncate">
                {{ job.label || jobKindLabel(job.kind) }}
              </p>
              <p class="text-xs text-gray-500 dark:text-gray-400">{{ statusLine(job) }}</p>
            </div>
            <button
              type="button"
              class="shrink-0 px-2 py-1 text-xs font-medium rounded-md text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
              :disabled="cancelling.has(job.id) || job.status === 'canceling'"
              @click.stop="onCancel(job)"
            >
              {{ cancelling.has(job.id) || job.status === 'canceling' ? 'Canceling…' : 'Cancel' }}
            </button>
          </div>
          <!-- progress bar -->
          <div class="mt-2 h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
            <div
              class="h-full rounded-full bg-blue-600 transition-[width] duration-500"
              :class="{ 'animate-pulse w-1/3': job.progress == null }"
              :style="job.progress != null ? { width: Math.round(job.progress * 100) + '%' } : null"
            />
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onBeforeUnmount } from 'vue'
import { useJobsStore } from '@/stores/jobs'
import { useUiStore } from '@/stores/ui'

const jobsStore = useJobsStore()
const uiStore = useUiStore()

const root = ref(null)
const open = ref(false)
const cancelling = reactive(new Set())

function jobKindLabel(kind) {
  if (kind === 'caption-translation') return 'Translating captions'
  if (kind === 'audio-dub') return 'Dubbing audio'
  return 'Working…'
}

function statusLine(job) {
  if (job.status === 'canceling') return 'Canceling…'
  if (job.progress == null) return 'Starting…'
  const pct = Math.round(job.progress * 100)
  return pct >= 100 ? 'Finishing…' : `Translating… ${pct}%`
}

async function onCancel(job) {
  cancelling.add(job.id)
  try {
    await jobsStore.cancel(job.id)
  } finally {
    cancelling.delete(job.id)
    if (!jobsStore.hasActive) open.value = false
  }
}

function onClickOutside(event) {
  if (!open.value) return
  const el = root.value
  if (!el) return
  const path = typeof event.composedPath === 'function' ? event.composedPath() : null
  const inside = path ? path.includes(el) : el.contains(event.target)
  if (!inside) open.value = false
}

function onKeydown(event) {
  if (event.key === 'Escape' && open.value) open.value = false
}

onMounted(() => {
  jobsStore.startPolling()
  document.addEventListener('click', onClickOutside)
  document.addEventListener('keydown', onKeydown)
})

onBeforeUnmount(() => {
  jobsStore.stopPolling()
  document.removeEventListener('click', onClickOutside)
  document.removeEventListener('keydown', onKeydown)
})
</script>
